import { Link } from "react-router-dom";
import { useState } from "react";

export default function Blog() {
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { id: "all", name: "All Articles", icon: "📚" },
    { id: "cheating-detection", name: "Cheating Detection", icon: "🔍" },
    { id: "time-saving", name: "Time-Saving Tips", icon: "⏰" },
    { id: "success-stories", name: "Success Stories", icon: "🌟" },
    { id: "best-practices", name: "Best Practices", icon: "💡" },
    { id: "security", name: "Security", icon: "🔒" }
  ];

  const featuredArticles = [
    {
      id: 1,
      title: "10 Warning Signs Your Students Are Cheating (And How to Stop Them)",
      excerpt: "Learn to identify suspicious behavior during digital exams and implement preventive measures that actually work.",
      category: "cheating-detection",
      readTime: "8 min read",
      publishDate: "December 15, 2024",
      image: "🔍",
      featured: true,
      views: "12.3K",
      tags: ["Cheating Detection", "Digital Exams", "Security"]
    },
    {
      id: 2,
      title: "How I Save 15+ Hours Per Week with AI Exam Creation",
      excerpt: "A high school teacher shares her complete workflow for using AI to create comprehensive exams in minutes instead of hours.",
      category: "time-saving",
      readTime: "12 min read", 
      publishDate: "December 10, 2024",
      image: "⏰",
      featured: true,
      views: "8.7K",
      tags: ["AI", "Time Management", "Productivity"]
    },
    {
      id: 3,
      title: "Teacher Success Story: From Burnout to Balance with ExamLock",
      excerpt: "Meet Sarah Johnson, who transformed her teaching experience and reclaimed her weekends using automated exam creation.",
      category: "success-stories",
      readTime: "6 min read",
      publishDate: "December 5, 2024", 
      image: "🌟",
      featured: true,
      views: "15.2K",
      tags: ["Success Stories", "Teacher Wellness", "Work-Life Balance"]
    }
  ];

  const articles = [
    ...featuredArticles,
    {
      id: 4,
      title: "The Complete Guide to Digital Cheating Prevention",
      excerpt: "Everything you need to know about preventing cheating in online exams, from basic precautions to advanced monitoring.",
      category: "cheating-detection",
      readTime: "15 min read",
      publishDate: "November 28, 2024",
      image: "🛡️",
      featured: false,
      views: "6.8K",
      tags: ["Cheating Prevention", "Online Testing", "Security"]
    },
    {
      id: 5,
      title: "5 Time-Saving Exam Templates Every Teacher Should Use",
      excerpt: "Ready-to-use exam formats that you can customize for any subject, saving hours of preparation time.",
      category: "time-saving",
      readTime: "7 min read",
      publishDate: "November 20, 2024",
      image: "📝",
      featured: false,
      views: "9.1K",
      tags: ["Templates", "Efficiency", "Exam Creation"]
    },
    {
      id: 6,
      title: "Why Traditional Proctoring Fails (And What Works Instead)",
      excerpt: "The limitations of traditional proctoring methods and how AI-powered monitoring provides better security.",
      category: "security",
      readTime: "10 min read",
      publishDate: "November 15, 2024",
      image: "🔒",
      featured: false,
      views: "4.9K",
      tags: ["Proctoring", "AI Technology", "Security"]
    },
    {
      id: 7,
      title: "Best Practices for Creating Effective Multiple Choice Questions",
      excerpt: "Expert tips for writing multiple choice questions that actually test understanding, not memorization.",
      category: "best-practices",
      readTime: "9 min read",
      publishDate: "November 10, 2024",
      image: "💡",
      featured: false,
      views: "7.3K",
      tags: ["Question Writing", "Assessment", "Best Practices"]
    },
    {
      id: 8,
      title: "From Paper to Digital: A School District's Transformation",
      excerpt: "How Lincoln School District successfully transitioned to digital testing and improved student outcomes.",
      category: "success-stories",
      readTime: "11 min read",
      publishDate: "November 5, 2024",
      image: "🏫",
      featured: false,
      views: "5.6K",
      tags: ["Digital Transformation", "Case Study", "School Districts"]
    }
  ];

  const filteredArticles = selectedCategory === "all" 
    ? articles 
    : articles.filter(article => article.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              📚 ExamLock Resources & Blog
            </h1>
            <p className="text-xl text-purple-100 mb-8 leading-relaxed">
              Expert insights, practical tips, and success stories to help you master 
              digital testing and save time with AI-powered exam creation.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-center">
              <div className="bg-white bg-opacity-20 px-6 py-3 rounded-lg">
                <div className="font-bold">50+ Articles</div>
                <div className="text-purple-200 text-sm">Expert insights</div>
              </div>
              <div className="bg-white bg-opacity-20 px-6 py-3 rounded-lg">
                <div className="font-bold">500+ Teachers</div>
                <div className="text-purple-200 text-sm">Reading weekly</div>
              </div>
              <div className="bg-white bg-opacity-20 px-6 py-3 rounded-lg">
                <div className="font-bold">New Posts</div>
                <div className="text-purple-200 text-sm">Every Tuesday</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Newsletter */}
      <div className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between max-w-4xl mx-auto gap-8">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-4">🔍 Search Articles</h2>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for topics, tips, or success stories..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button className="absolute right-3 top-3 text-gray-400 hover:text-gray-600">
                  <span className="text-xl">🔍</span>
                </button>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-4">📧 Weekly Newsletter</h2>
              <div className="flex">
                <input
                  type="email"
                  placeholder="your.email@school.edu"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-r-lg font-medium transition duration-200">
                  Subscribe
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">Get the latest tips and insights delivered to your inbox</p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center px-6 py-3 rounded-full font-medium transition duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-purple-50'
                }`}
              >
                <span className="mr-2">{category.icon}</span>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Featured Articles */}
      {selectedCategory === "all" && (
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">🌟 Featured Articles</h2>
              <p className="text-xl text-gray-600">Our most popular and impactful content</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {featuredArticles.map((article) => (
                <article key={article.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition duration-300">
                  <div className="p-8">
                    <div className="text-4xl mb-4 text-center">{article.image}</div>
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <span>{article.publishDate}</span>
                      <span className="mx-2">•</span>
                      <span>{article.readTime}</span>
                      <span className="mx-2">•</span>
                      <span>{article.views} views</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">{article.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{article.excerpt}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {article.tags.map((tag, index) => (
                        <span key={index} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <button className="text-purple-600 hover:text-purple-700 font-semibold transition duration-200">
                      Read Full Article →
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* All Articles */}
      <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              📖 {selectedCategory === "all" ? "All Articles" : categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            <p className="text-xl text-gray-600">
              {filteredArticles.length} articles found
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {filteredArticles.map((article) => (
              <article key={article.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition duration-300">
                <div className="p-6">
                  <div className="text-3xl mb-3 text-center">{article.image}</div>
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <span>{article.publishDate}</span>
                    <span className="mx-2">•</span>
                    <span>{article.readTime}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">{article.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{article.excerpt}</p>
                  <div className="flex justify-between items-center">
                    <button className="text-purple-600 hover:text-purple-700 font-semibold text-sm transition duration-200">
                      Read More →
                    </button>
                    <div className="text-xs text-gray-500">{article.views} views</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">
              🚀 Ready to Transform Your Testing?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Put these insights into practice with ExamLock's AI-powered exam creation 
              and advanced cheating detection. Join 500+ teachers saving 10+ hours per week.
            </p>
            <div className="space-y-4">
              <Link
                to="/teacher-register"
                className="inline-block bg-white text-blue-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition duration-200 shadow-lg mx-2"
              >
                🎯 Start Free Trial
              </Link>
              <Link
                to="/features"
                className="inline-block bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-blue-600 transition duration-200 mx-2"
              >
                📋 See All Features
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Subscribe Footer */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">📬 Never Miss an Update</h2>
            <p className="text-xl text-gray-600 mb-8">
              Get the latest articles, tips, and success stories delivered to your inbox every Tuesday.
            </p>
            <div className="flex max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-r-lg font-bold transition duration-200">
                Subscribe
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-3">
              Join 1,500+ educators • Unsubscribe anytime • No spam, ever
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}